'use client';

import React from 'react';
import { v4 as uuidV4 } from 'uuid';
import throttle from 'lodash/throttle';
import { usePathname, useSearchParams } from 'next/navigation';
import GlobalContext from '@/context/global-context';
import { TRACK_NAME } from '@/constants/site-track';
import { LOGIN_ROUTER_PATH, PRODUCTION_HOST } from '@/constants/router-path';
import {
  getCookie,
  getSessionStorage,
  setCookie,
  setSessionStorage,
} from '@/utils/storage';
import { sendEvent, useIntersectionObserver } from '@/utils/site-track';
import { isTimeStamp } from '@/utils/common-utils';

const SiteTrack = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams()?.toString() || '';

  const [trackObserverElements, setTrackObserverElements] = React.useState<
    Element[]
  >([]);
  const [historyUrl, setHistoryUrl] = React.useState<string[]>([]);

  const hasMoved = React.useRef(false);
  const hasFirstVisited = React.useRef(false);

  const trackMove = React.useCallback(
    throttle(() => {
      if (!hasMoved.current) {
        setCookie('_bi', Date.now().toString());
        hasMoved.current = true;
        sendEvent({ name: TRACK_NAME.move });
      }
    }, 300),
    [],
  );

  useIntersectionObserver(trackObserverElements);

  React.useEffect(() => {
    const referrer = document.referrer;
    const isLoginUrl = referrer.includes(window.origin + LOGIN_ROUTER_PATH);
    if (referrer) {
      if (!isLoginUrl) {
        GlobalContext.setContext('referer', referrer);
        setHistoryUrl([referrer]);
      } else {
        const beforeLoginUrl = sessionStorage.getItem('before_login_referer');
        if (beforeLoginUrl) {
          GlobalContext.setContext('referer', beforeLoginUrl);
          setHistoryUrl([beforeLoginUrl]);
        }
        sessionStorage.removeItem('before_login_referer');
      }
    }
    const cid = getCookie('cid');
    if (!hasFirstVisited.current && !cid) {
      hasFirstVisited.current = true;
      const cid = uuidV4().replaceAll('-', '');
      const sid = uuidV4().replaceAll('-', '');
      sessionStorage.setItem('cid', cid);
      sessionStorage.setItem('sid', sid);
      sendEvent({ name: TRACK_NAME.first_visit });
    }
    const session_start = getSessionStorage('session_start');
    if (!session_start) {
      setSessionStorage('session_start', Date.now().toString());
      sendEvent({ name: TRACK_NAME.session_start });
    }
    const _bi = getCookie('_bi');
    const isValidDate = _bi && isTimeStamp(_bi);
    if (!hasMoved.current && !isValidDate) {
      document.addEventListener('mousemove', trackMove);
      document.addEventListener('touchmove', trackMove);
    } else {
      hasMoved.current = true;
      document.removeEventListener('mousemove', trackMove);
      document.removeEventListener('touchmove', trackMove);
    }
    return () => {
      hasMoved.current = false;
      document.removeEventListener('mousemove', trackMove);
      document.removeEventListener('touchmove', trackMove);
    };
  }, []);

  React.useEffect(() => {
    if (pathname) {
      const renderEle = document.querySelector('body div');
      if (!renderEle) {
        return;
      }
      historyUrl.length > 0 &&
        GlobalContext.setContext('referer', historyUrl[historyUrl.length - 1]);
      const currentUrl = document.URL;
      const isLoginUrl = currentUrl.includes(window.origin + LOGIN_ROUTER_PATH);
      !isLoginUrl && setHistoryUrl((prev) => [...prev, currentUrl]);
      if (window.location.host === PRODUCTION_HOST) {
        const url = searchParams ? `${pathname}?${searchParams}` : pathname;
        try {
          (window as any)._hmt.push([TRACK_NAME.baidu_track, url]);
        } catch (e) {}
      }
      sendEvent({ name: TRACK_NAME.page_view });
      const obsElements = document.querySelectorAll('[data-observer-id]');
      setTrackObserverElements(obsElements as any);
    }
  }, [pathname, searchParams]);

  return null;
};

export default SiteTrack;
